import { Badge, Table, Text } from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'

import { useReportingI18n } from '../../../i18n'
import type { QueueCaller } from '../types'
import { CALLER_STATE_DISPLAY } from '../types'
import {
  setupHoldAlertTimer,
  shouldShowHoldAlert,
} from '../utils/holdAlertUtils'

export interface CallerRowProps {
  caller: QueueCaller
  holdAlertThreshold: number
  index: number
}

const STATUS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  Waiting: { bg: 'bg.muted', color: 'fg.muted' },
  Answered: { bg: 'green.subtle', color: 'green.fg' },
  Cancelled: { bg: 'gray.subtle', color: 'gray.fg' },
}

const Tr = Table.Row
const Td = Table.Cell

const formatWaitTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} sec`
  }
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (secs === 0) {
    return `${mins} min`
  }
  return `${mins} min, ${secs} sec`
}

const CallerRow: React.FC<CallerRowProps> = ({
  caller,
  holdAlertThreshold,
  index: _index,
}) => {
  const { text } = useReportingI18n()
  const [isHighlighted, setIsHighlighted] = useState(() =>
    shouldShowHoldAlert(caller, holdAlertThreshold),
  )

  const displayState =
    CALLER_STATE_DISPLAY[caller.state] || caller.state || 'Waiting'
  const translatedDisplayState = (() => {
    const statusMap: Record<string, string> = {
      Waiting: text('queueDashboard.status.waiting', 'Waiting'),
      Answered: text('queueDashboard.status.answered', 'Answered'),
      Cancelled: text('queueDashboard.status.cancelled', 'Cancelled'),
    }
    return statusMap[displayState] || displayState || 'Waiting'
  })()
  const formattedWaitTime = formatWaitTime(caller.score)
  const isWaiting = caller.state === 'Waiting' || caller.state === 'Trying'
  const waitTimeColor = isWaiting && isHighlighted ? 'red.solid' : 'fg.muted'

  const getBadgeColors = () => {
    if (displayState === 'Waiting' && isHighlighted) {
      return { bg: 'red.subtle', color: 'red.fg' }
    }
    return STATUS_BADGE_COLORS[displayState] || STATUS_BADGE_COLORS.Cancelled
  }
  const badgeColors = getBadgeColors()

  const handleAlert = useCallback(() => {
    setIsHighlighted(true)
  }, [])

  useEffect(() => {
    const currentlyHighlighted = shouldShowHoldAlert(caller, holdAlertThreshold)
    setIsHighlighted(currentlyHighlighted)

    if (isWaiting && !currentlyHighlighted) {
      const cleanup = setupHoldAlertTimer(
        caller,
        holdAlertThreshold,
        handleAlert,
      )
      return cleanup
    }

    return undefined
  }, [caller, holdAlertThreshold, handleAlert, isWaiting])

  const rowBg = isWaiting && isHighlighted ? 'red.subtle' : undefined

  return (
    <Tr
      data-testid={`caller-row-${caller.uuid}`}
      data-highlighted={isHighlighted}
      data-waiting-too-long={isHighlighted && isWaiting}
      borderBottom="1px solid"
      borderColor="border.default"
      _last={{ borderBottom: 'none' }}
      bg={rowBg}
    >
      <Td padding="12px 16px">
        <Text fontSize="0.875rem" color="fg.default">
          {caller.callerNumber || caller.cidNumber || '-'}
        </Text>
      </Td>

      <Td padding="12px 16px">
        <Text fontSize="0.875rem" color="fg.default">
          {caller.cidName || '-'}
        </Text>
      </Td>

      <Td padding="12px 16px">
        <Text fontSize="0.875rem" color={waitTimeColor}>
          {formattedWaitTime}
        </Text>
      </Td>

      <Td padding="12px 16px" textAlign="right">
        <Badge
          bg={badgeColors.bg}
          color={badgeColors.color}
          px="12px"
          py="4px"
          borderRadius="full"
          fontSize="0.75rem"
          fontWeight="500"
          textTransform="capitalize"
        >
          {translatedDisplayState}
        </Badge>
      </Td>
    </Tr>
  )
}

export default CallerRow
